import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DataTable, FilterChips, Modal, PageHeader, type ColumnDef, type FilterChipOption } from '@lipaflow/ui'
import { adminService, type PlatformAuditLogItem } from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { useOptionalSector } from '../sector/sector'

type View = 'all' | 'support' | 'custom'

// Quick views over the action filter. "Support sign-ins" is the record of
// staff entering a customer's app (sign in as a user, support access).
const SUPPORT_ACTIONS = ['impersonate_start', 'support_access_start']

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const when = (v: string) => new Date(v).toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const humanise = (code: string) => code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

/**
 * Activity log — who did what, for sensitive actions. In a sector: what
 * happened at its customers. Under Platform: what happened at no customer
 * (roles, platform switches).
 */
export default function AuditLogsPage() {
  const sector = useOptionalSector()
  usePageTitle(sector ? 'Activity log' : 'Platform activity')
  const [page, setPage] = useState(1)
  const [view, setView] = useState<View>('all')
  const [supportAction, setSupportAction] = useState(SUPPORT_ACTIONS[0])
  const [action, setAction] = useState('')
  const [resourceType, setResourceType] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<PlatformAuditLogItem | null>(null)

  const actionFilter = view === 'support' ? supportAction : view === 'custom' ? action.trim() : ''
  const { data, isLoading } = useQuery({
    queryKey: ['platform-audit-logs', sector?.code ?? 'platform', page, actionFilter, resourceType, search],
    queryFn: () =>
      adminService.listPlatformAuditLogs({
        vertical_code: sector?.code,
        page,
        per_page: 50,
        action: actionFilter || undefined,
        resource_type: resourceType.trim() || undefined,
        search: search.trim() || undefined,
      }),
  })

  const views: FilterChipOption<View>[] = [
    { key: 'all', label: 'Everything' },
    { key: 'support', label: 'Support sign-ins' },
    { key: 'custom', label: 'A specific action' },
  ]

  const columns: ColumnDef<PlatformAuditLogItem>[] = [
    { key: 'when', header: 'When', cell: (i) => <span className="text-body-sm text-ink whitespace-nowrap">{when(i.created_at)}</span> },
    { key: 'who', header: 'Who', cell: (i) => <span className="text-body-sm text-ink">{i.user_name || humanise(i.actor_type)}</span> },
    {
      key: 'what',
      header: 'What happened',
      cell: (i) => (
        <div className="max-w-[28rem]">
          <div className="text-body-sm text-ink line-clamp-2">{i.description || humanise(i.action)}</div>
          <div className="text-caption text-ink-faint font-mono">{i.action}</div>
        </div>
      ),
    },
    ...(sector
      ? [{ key: 'customer', header: 'Customer', hideOnMobile: true, cell: (i: PlatformAuditLogItem) => <span className="text-body-sm text-ink-muted">{i.tenant_name ?? '—'}</span> }]
      : []),
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={sector ? 'Activity log' : 'Platform activity'}
        subtitle={
          sector
            ? `Sensitive actions at ${sector.name} customers: who did what, when, and for which customer.`
            : 'Sensitive actions at no customer: roles, platform switches and other LipaFlow-wide changes.'
        }
      />

      <div className="space-y-3">
        <FilterChips options={views} value={view} onChange={(v) => { setView(v); setPage(1) }} />
        {view === 'support' && (
          <select aria-label="Kind of support sign-in" value={supportAction} onChange={(e) => { setSupportAction(e.target.value); setPage(1) }} className="rounded-button border border-hairline bg-surface p-2 text-body-sm text-ink">
            <option value="impersonate_start">Signed in as a customer's user</option>
            <option value="support_access_start">Opened support access</option>
          </select>
        )}
        {view === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            <input aria-label="Action code" value={action} onChange={(e) => { setAction(e.target.value); setPage(1) }} placeholder="Action code, e.g. tenant_update" className={`${field} font-mono`} />
            <input aria-label="Record type" value={resourceType} onChange={(e) => { setResourceType(e.target.value); setPage(1) }} placeholder="Record type (optional)" className={`${field} font-mono`} />
          </div>
        )}
      </div>

      <DataTable
        data={data?.items ?? []}
        columns={columns}
        getRowId={(i) => i.id}
        loading={isLoading}
        pagination={data ? { page, pageSize: 50, total: data.total } : undefined}
        onPageChange={setPage}
        searchable
        searchPlaceholder="Search what happened"
        searchValue={search}
        onSearch={(q) => { setSearch(q); setPage(1) }}
        onRowClick={setSelected}
        emptyState={{ title: 'Nothing matches', description: 'Try another view or search.' }}
      />

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? humanise(selected.action) : ''} size="lg">
        {selected && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Fact label="When" value={when(selected.created_at)} />
            <Fact label="Who" value={selected.user_name || humanise(selected.actor_type)} mono={selected.user_id} />
            <Fact label="Customer" value={selected.tenant_name || 'Platform'} mono={selected.tenant_id} />
            <Fact label="Record" value={humanise(selected.resource_type)} mono={selected.resource_id} />
            <div className="sm:col-span-2"><Fact label="What happened" value={selected.description || '—'} mono={selected.action} /></div>
          </dl>
        )}
      </Modal>
    </div>
  )
}

function Fact({ label, value, mono }: { label: string; value: React.ReactNode; mono?: string | null }) {
  return (
    <div>
      <dt className="text-caption text-ink-faint">{label}</dt>
      <dd className="text-body-sm text-ink mt-0.5">{value}</dd>
      {mono && <dd className="text-caption text-ink-muted font-mono break-all">{mono}</dd>}
    </div>
  )
}
